import { cn } from "@peerigon/pupper/tailwind";
import camelCase from "lodash/camelCase";
import { useTranslations } from "next-intl";
import React, { ReactNode, useRef } from "react";

import { BlogPostCategoriesType } from "src/enums";
import ArrowTip from "../../assets/arrowTip.svg";
import Title from "../Title/Title";

const CategoryTitle: React.FC<{
	children: ReactNode;
	isCurrent: boolean;
	onClick: () => void;
}> = ({ children, isCurrent, onClick }) => (
	<button
		type="button"
		className={cn(
			"font-bold whitespace-nowrap uppercase",
			"border-b-2 pb-[13px] lg:px-1.5",
			"text-px-12 leading-h-add-on lg:text-px-16 tracking-wide transition-all",
			isCurrent
				? "border-black font-bold"
				: "border-gray-line font-normal",
		)}
		onClick={onClick}
	>
		{children}
	</button>
);

const BlogCategoriesSwitch: React.FC<{
	currentCategory: BlogPostCategoriesType | undefined;
	setCategory: (category: BlogPostCategoriesType | undefined) => void;
	presentCategories: Array<BlogPostCategoriesType>;
}> = ({ currentCategory, setCategory, presentCategories }) => {
	const categoriesRef = useRef<HTMLDivElement>(null);
	const t = useTranslations();

	return (
		<>
			<Title headingLevel="h2" margin="100px 0 0 0">
				{t("byCategory")}
			</Title>
			<div
				className="sm-pr-0 gap-px-10 mt-[30px] flex overflow-x-auto pr-4 md:mt-[40px] lg:gap-[30px] lg:pt-[15px] xl:gap-[70px]"
				ref={categoriesRef}
			>
				<CategoryTitle
					isCurrent={!currentCategory}
					onClick={() => setCategory(undefined)}
				>
					{t("all")}
				</CategoryTitle>

				{presentCategories.map((category) => {
					return (
						<CategoryTitle
							key={category}
							onClick={() => setCategory(category)}
							isCurrent={currentCategory === category}
						>
							{t(camelCase(category) as keyof Messages)}
						</CategoryTitle>
					);
				})}
				<button
					type="button"
					className={cn(
						"h-px-60 w-px-60 absolute right-0 mt-[-21px] bg-transparent bg-linear-to-l from-white from-50% sm:hidden",
						"p-px-5 flex items-center justify-end",
					)}
					onClick={() =>
						categoriesRef.current?.scrollBy({
							top: 0,
							left: 100,
							behavior: "smooth",
						})
					}
					aria-label={t("scrollRight")}
				>
					<ArrowTip height="22px" />
				</button>
			</div>
			<div className="z-below-default border-gray-line mt-[-2px] border-b-2" />
		</>
	);
};

export default BlogCategoriesSwitch;
